/**
 * Verlanglijst (README §7.13): hartjes op kaarten en productpagina, teller in
 * de header, en de pagina /pages/verlanglijst die de bewaarde bieren toont.
 *
 * ponytail: de lijst staat in localStorage van deze browser. Genoeg om te
 * bewaren en later te bestellen; niet gedeeld tussen apparaten. Wil je dat wel,
 * dan hoort er een customer-metafield achter (app of Customer Account API).
 *
 * Een handle gaat alleen van de lijst bij een echte 404 (product weg). Faalt
 * het netwerk, dan blijft de lijst staan en zegt de pagina dat hij even niet
 * kon laden (onderzoek ronde 2, B7).
 *
 * "Zet alles in winkelwagen" zet alles wat op voorraad is in één cart/add.js
 * via voegToe uit smokkelaar-cart.js, zodat de lade opengaat en het statiegeld
 * meeloopt; uitverkochte bieren blijven staan en dat staat erbij (lijst C).
 */

import { voegToe, heeftLade, naarWinkelwagen } from '@theme/smokkelaar-cart';

const SLEUTEL = 'smokkelaar_verlanglijst';
const EVENT = 'smokkelaar:verlanglijst';
const root = () => window.Shopify?.routes?.root || '/';
const eur = (cent) => '€ ' + (cent / 100).toFixed(2).replace('.', ',');
/** '3 flessen' als alles bier is, anders '3 stuks' (een Smokkelbox is geen fles). */
const telling = (producten) =>
  producten.every((p) => p.type === 'Bier')
    ? `${producten.length} ${producten.length === 1 ? 'fles' : 'flessen'}`
    : `${producten.length} stuks`;
const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

const lees = () => {
  try { return JSON.parse(localStorage.getItem(SLEUTEL)) || []; } catch { return []; }
};
const schrijf = (lijst) => {
  try { localStorage.setItem(SLEUTEL, JSON.stringify(lijst)); } catch { /* privémodus: dan alleen deze pagina */ }
  document.dispatchEvent(new CustomEvent(EVENT, { detail: lijst }));
};
const heeft = (handle) => lees().includes(handle);
const wissel = (handle) => {
  const lijst = lees();
  const i = lijst.indexOf(handle);
  if (i >= 0) lijst.splice(i, 1); else lijst.push(handle);
  schrijf(lijst);
};

const HART = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M12 21s-7.5-4.6-9.6-9A5.4 5.4 0 0 1 12 6.3 5.4 5.4 0 0 1 21.6 12c-2.1 4.4-9.6 9-9.6 9z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>';

/**
 * Hartknop op een kaart of productpagina. De document-listener gaat weg zodra
 * het element uit de DOM gaat; Horizon rendert de kaarten bij elke filterwissel
 * opnieuw en anders stapelen de listeners op.
 */
class SmokkelaarHart extends HTMLElement {
  #ac = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#teken();
    this.addEventListener('click', (e) => {
      // De kaart eromheen is een link; het hartje mag niet navigeren.
      e.preventDefault();
      e.stopPropagation();
      wissel(this.dataset.handle);
    }, { signal });
    document.addEventListener(EVENT, () => this.#teken(), { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
  }

  #teken() {
    const aan = heeft(this.dataset.handle);
    this.classList.toggle('is-aan', aan);
    const knop = this.querySelector('button');
    if (!knop) return;
    knop.setAttribute('aria-pressed', String(aan));
    knop.setAttribute('aria-label', `${aan ? 'Verwijder van verlanglijst' : 'Bewaar op verlanglijst'}: ${this.dataset.naam || ''}`);
    const tekst = this.querySelector('[data-tekst]');
    if (tekst) tekst.textContent = aan ? this.dataset.tekstAan : this.dataset.tekstUit;
  }
}

/** Teller bij het hartje in de header. */
class SmokkelaarVerlanglijstTeller extends HTMLElement {
  #ac = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#teken();
    document.addEventListener(EVENT, () => this.#teken(), { signal });
    window.addEventListener('storage', () => this.#teken(), { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
  }

  #teken() {
    const n = lees().length;
    this.textContent = n ? String(n) : '';
    this.hidden = !n;
  }
}

/**
 * De pagina: haalt elk bewaard product op via /products/<handle>.js, plus de
 * meta-regel en het silhouet uit sections/smokkelaar-kaartdelen.liquid (die
 * kent .js niet). Lukt dat tweede niet, dan staat er alleen de brouwerij.
 */
class SmokkelaarVerlanglijst extends HTMLElement {
  #ac = null;
  #versie = 0;
  #beschikbaar = [];
  #overgeslagen = '';

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    document.addEventListener(EVENT, () => this.#render(), { signal });
    this.querySelector('[data-alles-knop]')?.addEventListener('click', (e) => this.#allesInWagen(e.currentTarget), { signal });
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
  }

  async #render() {
    const versie = ++this.#versie;
    const handles = lees();
    const lijst = this.querySelector('[data-lijst]');
    const leeg = this.querySelector('[data-leeg]');
    const fout = this.querySelector('[data-fout]');
    leeg.hidden = handles.length > 0;
    if (fout) fout.hidden = true;
    if (!handles.length) { lijst.innerHTML = ''; this.#toonAlles([], []); return; }

    // Per handle: { product, delen } bij succes, { weg: true } bij 404, { fout: true } bij al het andere.
    const resultaten = await Promise.all(
      handles.map((h) =>
        Promise.all([
          fetch(`${root()}products/${h}.js`, { headers: { Accept: 'application/json' } })
            .then((r) => {
              if (r.status === 404) return { weg: true };
              if (!r.ok) return { fout: true };
              return r.json().then((product) => ({ product })).catch(() => ({ fout: true }));
            })
            .catch(() => ({ fout: true })),
          fetch(`${root()}products/${h}?section_id=smokkelaar-kaartdelen`)
            .then((r) => (r.ok ? r.text() : ''))
            .catch(() => ''),
        ]).then(([resultaat, delen]) => ({ ...resultaat, delen }))
      )
    );
    // Een oudere render die later klaar is, mag de nieuwere niet overschrijven.
    if (versie !== this.#versie) return;

    // Producten die niet meer bestaan gaan stilletjes van de lijst; dat rendert opnieuw via het event.
    const weg = handles.filter((h, i) => resultaten[i].weg);
    if (weg.length) { schrijf(handles.filter((h) => !weg.includes(h))); return; }

    const producten = resultaten.filter((r) => r.product).map((r) => r.product);
    const kaarten = resultaten.filter((r) => r.product).map((r) => this.#kaart(r.product, r.delen));
    const mislukt = resultaten.some((r) => r.fout);
    // Kwam er niets binnen, dan blijven de kaarten van de vorige render staan.
    if (kaarten.length || !mislukt) {
      lijst.innerHTML = kaarten.join('');
      this.#toonAlles(producten.filter((p) => p.available), producten.filter((p) => !p.available));
    }
    if (fout) fout.hidden = !mislukt;
  }

  /**
   * Knop "Zet alles in winkelwagen (N flessen)": vanaf twee bewaarde bieren, als er
   * minstens één te koop is. Bij één bier doet de kaartknop hetzelfde; bij één
   * beschikbaar plus uitverkochte hoort de melding erbij welke we overslaan (QA lijst C).
   */
  #toonAlles(beschikbaar, uitverkocht) {
    this.#beschikbaar = beschikbaar;
    const blok = this.querySelector('[data-alles]');
    const knop = this.querySelector('[data-alles-knop]');
    const melding = this.querySelector('[data-alles-melding]');
    if (!blok || !knop || !melding) return;
    blok.hidden = beschikbaar.length < 1 || beschikbaar.length + uitverkocht.length < 2;
    knop.textContent = `Zet alles in winkelwagen (${telling(beschikbaar)})`;
    const namen = uitverkocht.map((p) => p.title);
    this.#overgeslagen = namen.length
      ? `${namen.length > 1 ? namen.slice(0, -1).join(', ') + ' en ' + namen.at(-1) : namen[0]} ${namen.length > 1 ? 'zijn' : 'is'} uitverkocht; die slaan we over.`
      : '';
    melding.textContent = this.#overgeslagen;
  }

  async #allesInWagen(knop) {
    const beschikbaar = this.#beschikbaar;
    const melding = this.querySelector('[data-alles-melding]');
    if (!beschikbaar.length) return;
    const items = beschikbaar.map((p) => ({ id: (p.variants.find((v) => v.available) || p.variants[0]).id, quantity: 1 }));
    knop.disabled = true;
    try {
      await voegToe(items, { bron: knop });
      if (melding) melding.textContent = `${telling(beschikbaar)} in je winkelwagen gezet. ${this.#overgeslagen}`.trim();
      if (!heeftLade()) naarWinkelwagen();
    } catch (err) {
      console.warn('[verlanglijst]', err);
      if (melding) melding.textContent = 'Dat lukte niet in één keer. Zet de bieren per kaart in je winkelwagen, of probeer het zo nog eens.';
    } finally {
      knop.disabled = false;
    }
  }

  #kaart(p, delen) {
    const variant = p.variants.find((v) => v.available) || p.variants[0];
    const url = `${root()}products/${p.handle}`;
    const beeld = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=480` : '';
    // Eigen server-HTML (Liquid, al ge-escaped); alleen de twee bekende delen overnemen.
    const html = delen ? new DOMParser().parseFromString(delen, 'text/html') : null;
    const meta = html?.querySelector('.smokkelaar-kaartmeta')?.outerHTML
      || (p.vendor ? `<p class="smokkelaar-kaartmeta">${esc(p.vendor)}</p>` : '');
    const fles = html?.querySelector('.smokkelaar-fles')?.outerHTML || '';
    return `
      <li class="smokkelaar-verlanglijst__kaart">
        <a class="smokkelaar-verlanglijst__beeld" href="${url}" tabindex="-1" aria-hidden="true">${beeld ? `<img src="${esc(beeld)}" alt="" loading="lazy" width="240" height="300">` : fles}</a>
        <div class="smokkelaar-verlanglijst__tekst">
          ${meta}
          <p class="smokkelaar-verlanglijst__naam"><a href="${url}">${esc(p.title)}</a></p>
          <p class="smokkelaar-verlanglijst__prijs">${eur(p.price)}</p>
        </div>
        <div class="smokkelaar-verlanglijst__acties">
          <form action="${root()}cart/add" method="post" data-smokkelaar-ajax>
            <input type="hidden" name="id" value="${variant.id}">
            <button type="submit" class="button"${p.available ? '' : ' disabled'}>${p.available ? 'Zet in winkelwagen' : 'Uitverkocht'}</button>
          </form>
          <smokkelaar-hart class="smokkelaar-hart smokkelaar-hart--kaart" data-handle="${esc(p.handle)}" data-naam="${esc(p.title)}">
            <button type="button" class="smokkelaar-hart__knop">${HART}</button>
          </smokkelaar-hart>
        </div>
      </li>`;
  }
}

for (const [naam, klasse] of [
  ['smokkelaar-hart', SmokkelaarHart],
  ['smokkelaar-verlanglijst-teller', SmokkelaarVerlanglijstTeller],
  ['smokkelaar-verlanglijst', SmokkelaarVerlanglijst],
]) {
  if (!customElements.get(naam)) customElements.define(naam, klasse);
}
